
import type { Product } from "@/types/common";

/**
 * Safely generates a product URL, prioritizing slug over ID
 */
export const generateProductUrl = (product: Product | null | undefined): string => {
  // If no product or no valid ID, return to products page
  if (!product || !product.id || product.id === 'null' || product.id === 'undefined') {
    console.warn('Invalid product data for URL generation:', product);
    return '/products';
  }

  // Prioritize slug if it exists and is valid
  if (product.slug && typeof product.slug === 'string' && product.slug.trim() !== '') {
    return `/product/${product.slug}`;
  }

  // Fall back to ID
  return `/product/${product.id}`;
};

/**
 * Validates if a product has the minimum required data for navigation
 */
export const isValidProductForNavigation = (product: any): product is Product => {
  return (
    product &&
    typeof product === 'object' &&
    product.id &&
    product.id !== 'null' &&
    product.id !== 'undefined' &&
    typeof product.id === 'string'
  );
};

/**
 * Safe product navigation handler
 */
export const handleProductNavigation = (
  product: any,
  navigate: (path: string) => void,
  fallbackPath: string = '/products'
) => {
  if (isValidProductForNavigation(product)) {
    navigate(generateProductUrl(product));
  } else {
    console.warn('Cannot navigate to product, redirecting to fallback:', fallbackPath);
    navigate(fallbackPath);
  }
};

/**
 * Extracts product identifier from URL (could be slug or ID)
 */
export const extractProductIdentifier = (urlParam: string): { isSlug: boolean; identifier: string } => {
  // Check if it's a UUID pattern (more comprehensive check)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const isUuid = uuidPattern.test(urlParam);
  
  // Also check for simple numeric IDs or other ID patterns
  const isNumericId = /^\d+$/.test(urlParam);
  const isSimpleId = /^[a-zA-Z0-9_-]+$/.test(urlParam) && urlParam.length < 10 && !urlParam.includes('-');
  
  return {
    isSlug: !isUuid && !isNumericId && !isSimpleId,
    identifier: urlParam
  };
};
